import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Print as PrintIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import image1 from '../../assets/riyal.png';

/**
 * InvoiceSummary component
 * Displays the invoice summary information including subtotal, VAT, total,
 * payment information, and action buttons
 * 
 * @param {Object} props
 * @param {Object} props.invoice - The invoice data object
 * @param {Function} props.calculateTotal - Function to calculate the total amount
 * @param {Function} props.calculateSubtotal - Function to calculate the subtotal
 * @param {Function} props.calculateVat - Function to calculate the VAT amount
 * @param {Function} props.getTotalPaid - Function to calculate the total amount paid
 * @param {Function} props.getRemainingBalance - Function to calculate the remaining balance
 * @param {Function} props.submitInvoice - Function to submit the invoice
 * @param {boolean} props.loading - Loading state for the submit button
 * @param {boolean} props.isEditMode - Whether the invoice is in edit mode
 */
const InvoiceSummary = ({
  invoice,
  calculateTotal,
  calculateSubtotal,
  calculateVat,
  getTotalPaid,
  getRemainingBalance,
  submitInvoice,
  loading,
  isEditMode
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: '4px',
        bgcolor: '#ffffff',
        border: '1px solid rgba(0, 0, 0, 0.12)'
      }}
    >
      {/* Summary Section */}
      <Box sx={{ mb: 1.5 }}>
        {/* Subtotal */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}>
          <Typography variant="body1">Subtotal</Typography>
          <Typography variant="body1" sx={{ mr: 1 }}>{calculateSubtotal(invoice.items).toFixed(2)}</Typography>
        </Box>

        {/* Additional Cost */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}>
          <Typography variant="body1">Additional Cost</Typography>
          <TextField
            value={invoice.additionalCost || ''}
            onChange={(e) => {
              if (typeof invoice.setInvoice === 'function') {
                invoice.setInvoice({ ...invoice, additionalCost: e.target.value });
              }
            }}
            type="number"
            size="small"
            InputProps={{
              inputProps: {
                min: 0,
                step: 0.01,
                style: { textAlign: 'right', padding: '6px 8px' }
              }
            }}
            sx={{
              width: '100px',
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                height: '32px'
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              }
            }}
          />
        </Box>

        {/* Discount */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}>
          <Typography variant="body1">Discount</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={invoice.discountPercentage || ''}
              onChange={(e) => {
                if (typeof invoice.setInvoice === 'function') {
                  const value = parseFloat(e.target.value) || 0;
                  const discountAmount = (calculateSubtotal(invoice.items) * value / 100).toFixed(2);
                  invoice.setInvoice({
                    ...invoice,
                    discountPercentage: e.target.value,
                    discountAmount: discountAmount
                  });
                }
              }}
              type="number"
              size="small"
              InputProps={{
                inputProps: {
                  min: 0,
                  max: 100,
                  step: 0.01,
                  style: { textAlign: 'right', padding: '6px 8px' }
                },
                endAdornment: <Typography sx={{ mr: 0.5 }}>%</Typography>
              }}
              sx={{
                width: '80px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  height: '32px'
                },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  display: 'none',
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                }
              }}
            />
            <Typography>=</Typography>
            <TextField
              value={invoice.discountAmount || ''}
              onChange={(e) => {
                if (typeof invoice.setInvoice === 'function') {
                  const value = parseFloat(e.target.value) || 0;
                  const subtotal = calculateSubtotal(invoice.items);
                  const percentage = subtotal > 0 ? ((value / subtotal) * 100).toFixed(2) : 0;
                  invoice.setInvoice({
                    ...invoice,
                    discountAmount: e.target.value,
                    discountPercentage: percentage
                  });
                }
              }}
              type="number"
              size="small"
              InputProps={{
                inputProps: {
                  min: 0,
                  step: 0.01,
                  style: { textAlign: 'right', padding: '6px 8px' }
                }
              }}
              sx={{
                width: '100px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  height: '32px'
                },
                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                  display: 'none',
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                }
              }}
            />
          </Box>
        </Box>

        {/* Taxable Amount - Shows Subtotal minus Discount */}
        <Divider sx={{ my: 1 }} />
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          bgcolor: '#f8faff',
          p: 1,
          borderRadius: 1
        }}>
          <Typography variant="body1" fontWeight={600}>Taxable Amount</Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mr: 1 }}>
            {(calculateSubtotal(invoice.items) + (parseFloat(invoice.additionalCost) || 0) - (parseFloat(invoice.discountAmount) || 0)).toFixed(2)}
          </Typography>
        </Box>

        {/* VAT */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}>
          <Typography variant="body1">VAT</Typography>
          <Typography variant="body1" sx={{ mr: 1 }}>{calculateVat(invoice.items).toFixed(2)}</Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Total */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: getTotalPaid() > 0 ? 1.5 : 0
        }}>
          <Typography variant="h6" fontWeight={600}>Total</Typography>
          <Typography variant="h6" fontWeight={600} sx={{ mr: 1, display: 'flex', alignItems: 'center' }}><img src={image1} alt="SAR" style={{ width: '16px', height: '16px', marginRight: '4px' }} />{calculateTotal(invoice.items).toFixed(2)}</Typography>
        </Box>

        {/* Payment Information */}
        {getTotalPaid() > 0 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: getRemainingBalance() > 0 ? '#fff4f4' : '#f0fff0',
            p: 1,
            borderRadius: 1,
            mt: 1.5
          }}>
            <Typography variant="body1" fontWeight={600}>
              {getRemainingBalance() > 0 ? 'Balance Due:' : 'Paid in Full'}
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              color={getRemainingBalance() > 0 ? "error.main" : "success.main"}
              sx={{ mr: 1 }}
            >
              {getRemainingBalance().toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          sx={{ borderRadius: 0 }}
        >
          Print
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={() => submitInvoice('submitted')}
          disabled={loading}
          sx={{ borderRadius: 0 }}
        >
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </Box>
    </Paper>
  );
};

export default InvoiceSummary;
